'use client';

import React from 'react';
import { ClipboardList } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Environment } from '@/libs/Environment';

/**
 * Props for the KeyTermsSheet component.
 * Used to display key terms before order creation.
 */
export interface KeyTermsSheetProps {
  /** Whether Keep My Price (KMP) feature is enabled */
  isKmpEnabled: boolean;
  /** Callback when user agrees to terms - receives timestamp and version */
  onAgree: (timestamp: string, version: string) => void;
  /** Optional: Callback when user declines (closes without agreeing) */
  onDecline?: () => void;
  /** Loading state for the agree button */
  isLoading?: boolean;
  /** Injected by ModalProvider - closes the modal */
  onClose?: () => void;
  /** Injected by ModalProvider - modal identifier */
  modalId?: string;
}

/**
 * KeyTermsSheet component displays essential terms users must acknowledge
 * before creating an order. Shows 2 or 3 terms based on KMP status.
 *
 * Terms shown always:
 * 1. "Your payments go toward this item only. This is not a savings account."
 * 2. "10% cancellation fee applies if you cancel your plan."
 *
 * Additional term when KMP is OFF:
 * 3. "Without Keep My Price, you'll pay more if the product price goes up."
 *
 * @example
 * ```tsx
 * <KeyTermsSheet
 *   isKmpEnabled={true}
 *   onAgree={(timestamp, version) => createOrder(timestamp, version)}
 *   isLoading={isCreatingOrder}
 * />
 * ```
 */
export const KeyTermsSheet: React.FC<KeyTermsSheetProps> = ({
  isKmpEnabled,
  onAgree,
  onDecline,
  isLoading = false,
  onClose,
}) => {
  const keyTerms = Environment.keyTerms;

  // Build the list of terms to display based on KMP status
  const termsToShow = React.useMemo(() => {
    const terms = [...keyTerms.always];
    if (!isKmpEnabled) {
      terms.push(...keyTerms.kmpOff);
    }
    return terms;
  }, [isKmpEnabled, keyTerms.always, keyTerms.kmpOff]);

  const handleAgree = () => {
    const timestamp = new Date().toISOString();
    onAgree(timestamp, keyTerms.version);
  };

  const handleDecline = () => {
    onDecline?.();
    onClose?.();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col items-center text-center">
        <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-blue-50">
          <ClipboardList className="size-6 text-[#0046C8]" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">
          Before you continue:
        </h2>
      </div>

      {/* Terms list */}
      <div className="mb-6">
        <ul className="space-y-4" aria-label="Key terms">
          {termsToShow.map((term, index) => (
            <li key={term} className="flex items-start gap-3 text-sm text-gray-700">
              <span
                className="flex size-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-900"
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <span>{term}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        <Button
          onClick={handleAgree}
          variant="primary"
          size="lg"
          className="w-full rounded-full"
          disabled={isLoading}
          loading={isLoading}
        >
          I Agree, Continue
        </Button>
        <Button
          onClick={handleDecline}
          variant="ghost"
          size="sm"
          className="w-full text-gray-500"
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

KeyTermsSheet.displayName = 'KeyTermsSheet';

export default KeyTermsSheet;
